import React, { memo, useCallback, useContext } from 'react'
import OfferPrice from 'components/Common/OfferPricing/Price/OfferPrice'
import { PricesContainer } from 'components/Common/OfferPricing/styles'
import useReduxStateListener from 'hooks/useReduxStateListener'
import { isLoggedIn } from 'selectors/accountSelectors'
import { pushWithRegion } from 'actions/NavigationActions'
import { getBookingPath } from 'lib/url/pathsUtils'
import { STEP_CAPACITY } from 'constants/booking'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import TextButton from 'components/Luxkit/Button/TextButton'
import { isCruiseV1Offer } from 'lib/offer/offerTypes'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import { useAppDispatch } from 'hooks/reduxHooks'

import Spacing from 'components/Common/Spacing'

import {
  PriceContainer,
} from 'components/Common/OfferPricing/styles'
import { rem } from 'polished'
import styled from 'styled-components'
import FlashTourSoldOutOrUnavailable from './FlashTourSoldOutOrUnavailable'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'

const StyledPriceContainer = styled(PriceContainer)`
  display: flex;
  flex-direction: column;
  padding-bottom: ${rem(6)};
  flex: 1;
`

const StyledSpacing = styled(Spacing)`
  margin-top: auto;
`

interface Props {
  memberId?: string;
  availableForPurchase: boolean;
  canInstantBook?: boolean;
  durationLabel: string;
  originAirport?: App.Airport;
  offerFlightsEnabled?: boolean;
  offer: App.TourOffer;
  packageOption: App.TourPackage;
  flightPrice?: number;
  shouldDisplayValue?: boolean;
  showFlights?: boolean;
  className?: string;
}

function FlashTourPackagePricing({
  memberId,
  durationLabel,
  packageOption,
  offer,
  availableForPurchase,
  originAirport,
  shouldDisplayValue,
  showFlights,
  offerFlightsEnabled,
  className,
  flightPrice,
}: Props) {
  const { type, typeLabel, flightDestinationPort, saleUnit, id: offerId } = offer
  const { id: packageId, discountPercent } = packageOption
  const { code: originAirportCode } = originAirport || {}

  const hasPrice = !!packageOption.price
  const flightCacheOff = showFlights && (offer.flightCacheDisabled || !offer.flightPrices[originAirport?.code])
  const originIsDestination = originAirport && flightDestinationPort === originAirportCode
  const bookingPath = getBookingPath(STEP_CAPACITY, {
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId,
    offerId,
  })

  const loginListener = useReduxStateListener<{ loggedIn: boolean }>((state) => {
    if (isLoggedIn(state)) {
      return { loggedIn: true }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  })

  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)

  const fireSelectPackageEvent = useCallback(() => {
    if (isCruiseV1Offer(offer)) {
      Analytics.track('client-event', clientEvent('select_package', 'clicked', 'cruises_v1', 'interaction'))
    }
  }, [offer])

  const showRegisterModal = useCallback(async() => {
    const hotelOfferDetails: App.AccountModalOfferDetails = {
      title: offer.name,
      duration: packageOption.duration,
      value: packageOption.price,
      imageId: offer.image.id,
      imageTitle: offer.name,
    }

    await showModal<AccountAccessModalResult>(<AccountAccessModal
      initialMode="login"
      hotelOfferDetails={hotelOfferDetails}
    />)

    loginListener.awaitResult().then(({ loggedIn }) => {
      if (loggedIn) {
        fireSelectPackageEvent()

        dispatch(pushWithRegion(bookingPath))
      }
    })
  }, [bookingPath, dispatch, loginListener, offer, packageOption, showModal, fireSelectPackageEvent])

  return <PricesContainer className={className}>
    <StyledPriceContainer>
      <OfferPrice
        offer={offer}
        durationLabel={durationLabel}
        offerSaleUnit={saleUnit}
        label={typeLabel}
        shouldShowValue={shouldDisplayValue}
        showFlights={showFlights}
        price={packageOption.price}
        taxesAndFees={packageOption.taxesAndFees}
        value={packageOption.value}
        discountPercent={discountPercent}
        showDiscountPill
        type={type}
      />
      <FlashTourSoldOutOrUnavailable
        availableForPurchase={availableForPurchase}
        hasPrice={hasPrice}
      >
        <StyledSpacing pt={12}>
          <TextButton
            kind="primary"
            fit="flex"
            data-testid="TourBookingBtn"
            {...(!memberId ? { onClick: showRegisterModal } : { to: bookingPath, onClick: fireSelectPackageEvent })}
          >
            Check Availability
          </TextButton>
        </StyledSpacing>
      </FlashTourSoldOutOrUnavailable>
    </StyledPriceContainer>
    {originAirport && showFlights && offerFlightsEnabled &&
      <StyledPriceContainer>
        <OfferPrice
          offer={offer}
          hasClause
          isFlightPrice
          flightCacheOff={flightCacheOff}
          durationLabel={durationLabel}
          offerSaleUnit="package"
          label={`${typeLabel} + Flights`}
          shouldShowValue={shouldDisplayValue && !flightCacheOff}
          showFlights={showFlights}
          price={flightPrice && packageOption.price + flightPrice}
          taxesAndFees={flightPrice && packageOption.taxesAndFees}
          value={flightPrice && packageOption.value + flightPrice}
          data-testid="pricing-with-flights"
          originAirport={originAirport}
          noFlightsFromOrigin={originIsDestination}
          discountPercent={discountPercent}
          type={type}
        />
        <FlashTourSoldOutOrUnavailable
          availableForPurchase={availableForPurchase}
          hasPrice={hasPrice}
        />
      </StyledPriceContainer>
    }
  </PricesContainer>
}

export default memo(FlashTourPackagePricing)
